import React, { useEffect, useReducer, useState } from "react";
import PreQualificationContext from "./preQualificationContext.jsx";
import PreQualificationReducer from "./preQualificationReducer.jsx";
import * as types from "./types";
import { pageName } from "../../config/pageInfo/pageNames.js";

//** Utilities */
import { toUpperFirstCharacterAllWords } from "../../utilities/mappingHelper.js";
import { currencyStringToNumber } from "../../utilities/numericFormat.js";
import { ageUpdate } from "../../utilities/ageRangeMapping.js";
import {
  matchOccupationList,
  matchInsuranceList,
  isAdditionalPolicyPresent,
  isAdditionalPolicyPresentRetrieve,
} from "../../utilities/preQualificationUtils.js";

import { isObjectEmpty } from "../../utilities/utils.js";

//** Config  */
import { Application, AGE_RANGE } from "../../config/const/enums";

//----------------------------------------------------------------

const PreQualificationState = (props) => {
  const initialDataItems = {
    residentQuestion: null,
    title: "",
    firstName: "",
    surname: "",
    gender: "",
    dob: null,
    age: 0,
    maturityAge: 0,
    minAgeGroup: 0,
    maxAgeGroup: 0,
    disclaimerAgeRange: AGE_RANGE.BELOW,
    showAdditionalCover: true,
    emailAddress: "",
    contactNumber: "",
    state: "",
    promoCode: "",
    tobaccoQuestion: null,
    otherAllianzInsuranceQuestion: null,
    otherAllianzInsuranceList: [],
    lifeCover: "",
    illnessCover: "",
    shouldUseIllnessCover: true, //* When we are on Outcome and there is an upsell, has it bee added.
    permanentDisabilityCover: "",
    traumaCover: "",
    occupationList: "",
    termsAndConditions: false,
    acceptOffer: null,
    callbackConsent: true,
    // callbackConsent: null, //* Does the online user give explicit consent to be contacted.
  };

  //----------------------------------------------------------------
  const commonProperties = {
    numberOfApplicants: 1,
    quoteNumber: "",
    monthlyPremium: 0,
    yearlyPremium: 0,
    discountApplied: false,
    percentageDiscountApplied: 0,
    bothAddressesTheSame: null,
    policyMailingAddressFull: "",
    policyMailingAddressFullGuid: "",
    policyMailingAddressLevelNumber: "",
    policyMailingAddressUnitNumber: "",
    policyMailingAddressStreetNumber: "",
    policyMailingAddressStreetNumberType: "",
    policyMailingAddressStreetName: "",
    policyMailingAddressStreetNameType: "",
    policyMailingAddressPostcodeSuburb: "",
    residentialAddressFull: "",
    residentialAddressFullGuid: "",
    residentialAddressLevelNumber: "",
    residentialAddressUnitNumber: "",
    residentialAddressStreetNumber: "",
    residentialAddressStreetNumberType: "",
    residentialAddressStreetName: "",
    residentialAddressStreetNameType: "",
    residentialAddressPostcodeSuburb: "",
    paymentFrequencyUsed: "monthly", //* The payment frequency used in the final payment - Needed on the confirmation page
    policyDocumentRequestType: "yes", //* yes = "E" or no = “P” (no is only allowed to be set through call centre flow)
    isBankAccountPayment: false,
    bankPaymentPermissionGranted: null,
    bankBSB: "",
    bankAccountNumber: "",
    bankAccountName: "",
    bankNameOfFinancialInstitution: "",
    updateUser: false, //* Used when we need to update Okta details.
    loaded: false,
    quoteCreated: false,
    customerRetrieveQuoteLoaded: false, //* Did we load this data from the retrieveQuote API?
    auraOutcomeLoaded: false, //* Did we load this data from the interviewOutcome API?
    policyOutcomeAcceptTerms: false,
    policyOutcomeAcceptUpgrade: false,
    existingPolicyNumber: "",
    additionalPolicyHolderTitle: "",
    additionalPolicyHolderFirstName: "",
    additionalPolicyHolderSurname: "",
    additionalPolicyHolderDOB: "",
    personOne: {
      ...initialDataItems,
    },
    personTwo: {
      ...initialDataItems,
    },
  };

  const initialState = {
    ...commonProperties,
    application: Application.ONLINE,
    promoCodeValid: false,
    dataUpdating: false,
    additionalConditions: [
      {
        additionalConditionsLife: {
          loading: 0,
          exclusionCodes: [],
        },
      },
      {
        additionalConditionsCriticalIllness: {
          loading: 0,
          exclusionCodes: [],
        },
      },
      {
        additionalConditionsUnableToWork: {
          loading: 0,
          exclusionCodes: [],
        },
      },
    ],
  };

  const initialStateCC = {
    ...commonProperties,
    application: Application.CONTACT_CENTER,
    promoCodeValid: false,
    dataUpdating: false,
    privacyRecordingQuestion: null,
    additionalSummaryQuestion: null,
    optOutDirectMarketingQuestion: false,
    additionalConditions: [
      {
        additionalConditionsLife: {
          loading: 0,
          exclusionCodes: [],
        },
      },
      {
        additionalConditionsCriticalIllness: {
          loading: 0,
          exclusionCodes: [],
        },
      },
      {
        additionalConditionsUnableToWork: {
          loading: 0,
          exclusionCodes: [],
        },
      },
    ],
  };

  // const checkValueChange = (from, newState, value) => {
  // 	const v = value;
  // 	const st = state;
  // 	const ns = newState;

  // 	console.log(
  // 		`checkValueChange: from: ${from} - current State: ${st?.personOne?.lifeCover?.toString()} - new State: ${ns?.personOne?.lifeCover?.toString()} - value: ${JSON.stringify(
  // 			value
  // 		)}`
  // 	);

  // 	if (
  // 		st?.personOne?.lifeCover?.toString() == "135000" ||
  // 		ns?.personOne?.lifeCover?.toString() == "135000"
  // 	) {
  // 	}
  // };

  //----------------------------------------------------------------
  const [applicationType, setApplicationType] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // const [oktaUpdateRequired, setOKtaUpdateRequired] = useState(false);

  // const handleOktaUpdateRequired = async (value) => {
  //   setOKtaUpdateRequired(value);
  //   await new Promise((resolve) => setTimeout(resolve, 0));
  // };

  //** Initial State */
  const [initialStateData, setInitialStateData] = useState(null);

  //** Sub-pages */
  // const [applicationSubPage, setApplicationSubPage] = useState(null);

  //? Application Entry Point: Put anything in here that needs to happen before we show the
  //? first page.
  const initializing = async (application) => {
    //* Set the application type (Online, ContactCenter)
    //* on first load.
    setApplicationType(application);
    // setInitialized(true);
  };

  useEffect(() => {
    switch (applicationType) {
      case Application.ONLINE:
        setInitialStateData(initialState);
        // setApplicationSubPage(PATH_ONLINE_SUB);
        break;
      case Application.CONTACT_CENTER:
        setInitialStateData(initialStateCC);
        // setApplicationSubPage(PATH_CONTACT_CENTER_SUB);
        break;
      default:
        setInitialStateData(null);
        // setApplicationSubPage(null);
        break;
    }
  }, [applicationType]);

  useEffect(() => {
    //** Update the state */
    if (initialStateData == null) return;

    // initialStateData.loaded = true;

    dispatch({ type: types.UPDATE_DATA, payload: initialStateData });
  }, [initialStateData]);

  useEffect(() => {
    if (initialStateData == null) return;

    if (initialStateData?.loaded === true) {
      setInitialized(true);
    }
  }, [initialStateData?.loaded]);

  //----------------------------------------------------------------

  const [state, dispatch] = useReducer(
    PreQualificationReducer,
    initialStateData
  );

  // const resetData = () => {
  // 	const initialDataState =
  // 		applicationType === Application.ONLINE ? initialState : initialStateCC;
  // 	dispatch({ type: types.UPDATE_DATA, payload: initialDataState });
  // };

  const handleInputChange = async ({ name, value }) => {
    let updateState = { ...state, [name]: value };

    // checkValueChange("handleInputChange", updateState, { name, value });
    dispatch({ type: types.UPDATE_DATA, payload: updateState });
  };

  //* Ensure that the first and last name (and maybe others) are the correct case.
  //* These values are displayed in the UI.
  const correctCaseItems = ["firstName", "surname"];
  const correctCase = (key, item) => {
    let setCorrectCase = item;
    if (correctCaseItems.includes(key)) {
      setCorrectCase = toUpperFirstCharacterAllWords(item);
    }
    return setCorrectCase;
  };

  const checkForChangeItems = [
    "firstName",
    "surname",
    "emailAddress",
    "contactNumber",
  ];
  const checkForChange = async (values, updateOktaUserRequired) => {
    let currentState = structuredClone(state);
    if (updateOktaUserRequired === false) return false;

    let updateRequired = currentState.updateUser;
    checkForChangeItems.forEach((item) => {
      if (
        currentState.personOne[item] !== "" &&
        values.personOne[item] !== currentState.personOne[item]
      ) {
        updateRequired = true;
      }
    });
    return updateRequired;
  };

  const contactPaymentDetails = async (values) => {
    if (!values) return;

    const updatedContactDetails = {
      bothAddressesTheSame: values?.bothAddressesTheSame,

      policyMailingAddressFull: values?.policyMailingAddressFull,
      policyMailingAddressFullGuid: null,
      policyMailingAddressLevelNumber: values?.policyMailingAddressLevelNumber,
      policyMailingAddressUnitNumber: values?.policyMailingAddressUnitNumber,
      policyMailingAddressStreetNumber:
        values?.policyMailingAddressStreetNumber,
      policyMailingAddressStreetNumberType:
        values?.policyMailingAddressStreetNumberType,
      policyMailingAddressStreetName: values?.policyMailingAddressStreetName,
      policyMailingAddressStreetNameType:
        values?.policyMailingAddressStreetNameType,
      policyMailingAddressPostcodeSuburb:
        values?.policyMailingAddressPostcodeSuburb,

      residentialAddressFull: values?.residentialAddressFull,
      residentialAddressFullGuid: null,
      residentialAddressLevelNumber: values?.residentialAddressLevelNumber,
      residentialAddressUnitNumber: values?.residentialAddressUnitNumber,
      residentialAddressStreetNumber: values?.residentialAddressStreetNumber,
      residentialAddressStreetNumberType:
        values?.residentialAddressStreetNumberType,
      residentialAddressStreetName: values?.residentialAddressStreetName,
      residentialAddressStreetNameType:
        values?.residentialAddressStreetNameType,
      residentialAddressPostcodeSuburb:
        values?.residentialAddressPostcodeSuburb,
    };

    return updatedContactDetails;
  };

  //**  */
  function replacedKeysInData(values, person) {
    let map = {}; // Holds replaced values
    let replacedMap = { [`${person}`]: {} }; // Creates an empty object for the persons details

    //* Loop over every item and if the key of the
    //* item contains the person string ("personOne" or "personTwo")
    //* remove the person prefix and add it to the map object
    Object.keys(values).forEach((key) => {
      if (key.includes(person)) {
        const newKey = key.replace(`${person}_`, "");
        map[newKey] = correctCase(newKey, values[key]);
      }
    });

    replacedMap[`${person}`] = { ...map }; // Spread the new values in to the replaceMap object
    return replacedMap;
  }
  //**  */

  //* Just check if a given object is empty before we try to do any operations on it.
  function isObjEmpty(obj) {
    return Object?.keys(obj)?.length === 0;
  }

  const spreadMergeData = async (values, updateOktaUserRequired) => {
    const pOne = replacedKeysInData(values, "personOne");
    const pTwo = replacedKeysInData(values, "personTwo");

    const dualApplicant = pTwo?.personTwo?.firstName?.length;

    let isDualApplication = isObjEmpty(pTwo.personTwo);
    const numberOfPeople =
      isDualApplication === false && dualApplicant > 0 ? 2 : 1;

    const updateOktaUser = await checkForChange(pOne, updateOktaUserRequired);

    const marketingOptOut =
      values.optOutDirectMarketingQuestion === "yes"
        ? true
        : values.optOutDirectMarketingQuestion === "no"
          ? false
          : false;
    const callbackConsent =
      values.callbackConsent == null
        ? null
        : values.callbackConsent === "yes"
          ? true
          : false;
    const temp = {
      numberOfApplicants: numberOfPeople,
      updateUser: updateOktaUser,
      optOutDirectMarketingQuestion: marketingOptOut,
    };

    const mapPaymentContactDetails = await contactPaymentDetails(values);

    //----------------------------------------------------------------

    let mergeMapPOne = { [`${"personOne"}`]: {} };
    mergeMapPOne[`${"personOne"}`] = {
      ...state["personOne"],
      ...pOne["personOne"],
    };

    // //* Second applicant.
    let mergeMapPTwo = { [`${"personTwo"}`]: {} };
    mergeMapPTwo[`${"personTwo"}`] = {
      ...state["personTwo"],
      ...pTwo["personTwo"],
    };

    let stateSpread = {
      ...state,
      ...mergeMapPOne,
      ...mergeMapPTwo,
      ...temp,
      ...mapPaymentContactDetails,
    };
    return stateSpread;
  };

  //** Remove second applicant */
  //** and set numberOfApplicants back to 1 */
  const removeSecondApplicant = () => {
    //TODO:
    // Reset all values and set the num
    let mergeMapPTwo = { [`${"personTwo"}`]: {} };

    mergeMapPTwo[`${"personTwo"}`] = {
      ...initialDataItems,
    };

    // mergeMapPTwo[`${"personTwo"}`] = {
    //   ...state["personTwo"],
    //   ...initialDataItems,
    // };

    const temp = { numberOfApplicants: 1 };
    let stateSpread = { ...state, ...mergeMapPTwo, ...temp };

    dispatch({ type: types.UPDATE_DATA, payload: stateSpread });
  };

  const mapPaymentMethodDetails = async (mappedValues, values) => {
    //* Map the payments if they payment method is Bank Account
    //! DO NOT map the Credit Card detail.
    //! The credit card details should ever touch our servers.
    if (!state.isBankAccountPayment) return;

    mappedValues.bankBSB = values.bankBSB;
    mappedValues.bankAccountNumber = values.bankAccountNumber;
    mappedValues.bankAccountName = values.bankAccountName;
    mappedValues.bankNameOfFinancialInstitution =
      values.bankNameOfFinancialInstitution;
    mappedValues.bankPaymentPermissionGranted =
      values.bankPaymentPermissionGranted;
  };

  //** Payment Contact Details */
  const mapPaymentContactDetails = async (mappedValues, values) => {
    //* We need to map over each address 'type'.
    try {
      const addressItems = ["policyMailingAddress", "residentialAddress"];
      if (values?.policyDocumentRequestType === "no") {
        mappedValues.policyDocumentRequestType =
          values.policyDocumentRequestType;
      }
      addressItems.map((items) => {
        Object.keys(values).forEach((key) => {
          if (key.includes(items)) {
            const valueItem = key;
            switch (valueItem) {
              case "residentialAddressFull":
                mappedValues.residentialAddressFull = values[key]?.fullAddress;
                mappedValues.residentialAddressFullGuid = values[key]?.id;
                break;
              case "policyMailingAddressFull":
                mappedValues.policyMailingAddressFull =
                  values[key]?.fullAddress;
                mappedValues.policyMailingAddressFullGuid = values[key]?.id;
                break;
              case "residentialAddressPostcodeSuburb":
                const isFullAddress = values[key]?.fullAddress?.length > 0;
                mappedValues.residentialAddressPostcodeSuburb =
                  values[key]?.fullAddress;

                if (isFullAddress === false) {
                  mappedValues.residentialAddressLevelNumber =
                    values["residentialAddressLevelNumber"];
                  mappedValues.residentialAddressUnitNumber =
                    values["residentialAddressUnitNumber"];
                  mappedValues.residentialAddressStreetNumber =
                    values["residentialAddressStreetNumber"];
                  mappedValues.residentialAddressStreetNumberType =
                    values["residentialAddressStreetNumberType"];
                  mappedValues.residentialAddressStreetName =
                    values["residentialAddressStreetName"];
                  mappedValues.residentialAddressStreetNameType =
                    values["residentialAddressStreetNameType"];
                  mappedValues.residentialAddressPostcodeSuburb =
                    values["residentialAddressPostcodeSuburb"];
                }
                break;
              case "policyMailingAddressPostcodeSuburb":
                const isFullMailingAddress =
                  values[key]?.fullAddress?.length > 0;
                mappedValues.policyMailingAddressPostcodeSuburb =
                  values[key]?.fullAddress;

                if (isFullMailingAddress === false) {
                  mappedValues.policyMailingAddressLevelNumber =
                    values["policyMailingAddressLevelNumber"];
                  mappedValues.policyMailingAddressUnitNumber =
                    values["policyMailingAddressUnitNumber"];
                  mappedValues.policyMailingAddressStreetNumber =
                    values["policyMailingAddressStreetNumber"];
                  mappedValues.policyMailingAddressStreetNumberType =
                    values["policyMailingAddressStreetNumberType"];
                  mappedValues.policyMailingAddressStreetName =
                    values["policyMailingAddressStreetName"];
                  mappedValues.policyMailingAddressStreetNameType =
                    values["policyMailingAddressStreetNameType"];
                  mappedValues.policyMailingAddressPostcodeSuburb =
                    values["policyMailingAddressPostcodeSuburb"];
                }
                break;
              default:
                mappedValues[key] = values[key];
                break;
            }
          }
        });
      });
    } catch (ex) {
      console.log("Error occurred - ", ex);
    }
  };
  //** */
  const mapAdditionalPolicyHolderDetails = async (mappedValues, values) => {
    mappedValues.additionalPolicyHolderTitle =
      values.additionalPolicyHolderTitle;
    mappedValues.additionalPolicyHolderFirstName =
      values.additionalPolicyHolderFirstName;
    mappedValues.additionalPolicyHolderSurname =
      values.additionalPolicyHolderSurname;
    mappedValues.additionalPolicyHolderDOB = values.additionalPolicyHolderDOB;
  };

  const setAdditionalPolicyHolderDefault = async (mappedValues) => {
    mappedValues.additionalPolicyHolderTitle = "";
    mappedValues.additionalPolicyHolderFirstName = "";
    mappedValues.additionalPolicyHolderSurname = "";
    mappedValues.additionalPolicyHolderDOB = "";
  };

  const dispatchWithCallback = (action, callback) => {
    dispatch(action);
    callback && callback();
  };

  //** */
  const setValues = async (
    values,
    currentPage = "",
    updateOktaUserRequired = false
  ) => {
    if (!isObjEmpty(state)) {
      setDataUpdating(true);
    }

    //* Please note that the use of setTimeout with a delay of 0 milliseconds is used here to simulate a micro-task queue,
    //* which allows the component to wait for the next tick of the event loop before updating the local state.
    //* This is not a heavy delay and should not cause any noticeable delay in the application.
    //* It allows us to be sure that the loading state of our state object is set.
    await new Promise((resolve) => setTimeout(resolve, 0));

    try {
      let mappedValuesOne =
        isObjEmpty(values) === true
          ? { ...state }
          : await spreadMergeData(values, updateOktaUserRequired);
      mappedValuesOne.personOne = ageUpdate(
        mappedValuesOne.personOne,
        mappedValuesOne.personOne.dob,
        state.application
      );
      mappedValuesOne.personTwo = ageUpdate(
        mappedValuesOne.personTwo,
        mappedValuesOne.personOne.dob,
        state.application
      );

      // Map the additional policy holder details in the state from the form data
      if (isAdditionalPolicyPresent(values)) {
        mapAdditionalPolicyHolderDetails(mappedValuesOne, values);
      } else {
        // when user goes back and removes the additional policy holder we need to set it to default in the state.
        // It is the value from the state that is used in createQuoteMapping to call the createQuote API
        setAdditionalPolicyHolderDefault(mappedValuesOne);
      }
      //* Map the Payment Contact Details
      //* Note: We also have to if we are on the Payment Contacts page
      //* completed the address and moved back to the Outcome page
      //* and forward again.
      if (
        currentPage === pageName.PP_CONTACT ||
        (currentPage === pageName?.PP_ONLINE_POLICY_OUTCOME &&
          values?.policyMailingAddressFull?.id != null)
      ) {
        // Update the Payment Contact Details
        mapPaymentContactDetails(mappedValuesOne, values);
      }

      //* Map the Payment Method details.
      //TODO: MAP PAYMENT METHOD
      if (currentPage === pageName.PP_ONLINE_PAYMENT) {
        mapPaymentMethodDetails(mappedValuesOne, values);
      }

      // checkValueChange("setValues", mappedValuesOne, values);
      dispatchWithCallback({
        type: types.UPDATE_DATA,
        payload: { ...mappedValuesOne },
      });
    } catch (e) {
      console.log("Error occurred ", e);
    }
  };

  //----------------------------------------------------------------
  const updateCriticalIllness = async (values) => {
    if (isObjEmpty(state)) return;
    if (
      values.amount === undefined &&
      (values.type === "add" || values.type === "update_question")
    )
      return; //* I don't know why I put this here? Keep an eye on it.
    // if (values.amount === undefined) return;

    const amt =
      values?.amount != null
        ? parseFloat(currencyStringToNumber(values?.amount), 10)
        : "";

    if (!isObjEmpty(state)) {
      setDataUpdating(true);
    }
    await new Promise((resolve) => setTimeout(resolve, 0));

    let mappedValuesTwo = {};
    let newState = {};

    switch (values.type) {
      case "add": //? Made not need this.
        mappedValuesTwo = {
          ...state,
          ...(state[values.person].illnessCover = amt),
        };
        break;
      case "remove":
        newState = { ...state, ...(state[values.person].traumaCover = "") };
        delete newState[values.person].illnessCover;
        mappedValuesTwo = { ...newState };
        break;
      case "update_question":
        newState = {
          ...state,
          ...(state[values.person].illnessCover = amt),
          ...(state[values.person].traumaCover = "no"),
        };
        mappedValuesTwo = { ...newState };
        break;
      case "update_use_flag":
        newState = {
          ...state,
          ...(state[values.person].shouldUseIllnessCover =
            values?.shouldUseIllnessCover),
        };
        mappedValuesTwo = { ...newState };
        break;
      default:
        break;
    }
    // checkValueChange("updateCriticalIllness", mappedValuesTwo, values);

    dispatchWithCallback({
      type: types.UPDATE_DATA,
      payload: { ...mappedValuesTwo },
    });
  };

  const updatePermanentDisabilityCover = async (values) => {
    if (isObjEmpty(state)) return;

    const isRemoveType =
      values.type === "remove_keep_occupation" || values.type === "remove";

    if (values.amount === undefined && !isRemoveType) return;

    const amt =
      values?.amount != null
        ? parseFloat(currencyStringToNumber(values?.amount), 10)
        : "";

    if (!isObjEmpty(state)) {
      setDataUpdating(true);
    }
    await new Promise((resolve) => setTimeout(resolve, 0));

    let mappedValuesThree = {};
    let newState = {};

    switch (values.type) {
      case "add": //? Made not need this.
        mappedValuesThree = {
          ...state,
          ...(state[values.person].permanentDisabilityCover = amt),
        };
        break;
      case "remove":
        newState = { ...state, ...(state[values.person].occupationList = "") };
        delete newState[values.person].permanentDisabilityCover;
        mappedValuesThree = { ...newState };
        break;
      case "remove_keep_occupation":
        newState = { ...state };
        delete newState[values.person].permanentDisabilityCover;
        mappedValuesThree = { ...newState };
        break;
      case "update_question":
        newState = {
          ...state,
          ...(state[values.person].permanentDisabilityCover = amt),
          ...(state[values.person].occupationList = values.updateField),
        };
        mappedValuesThree = { ...newState };
        break;
      default:
        break;
    }

    // checkValueChange(
    // 	"updatePermanentDisabilityCover",
    // 	mappedValuesThree,
    // 	values
    // );

    dispatchWithCallback({
      type: types.UPDATE_DATA,
      payload: { ...mappedValuesThree },
    });
  };

  //* Set the state to updating.
  const setDataUpdating = (payloadValue) =>
    dispatchWithCallback({
      type: types.UPDATE_DATA_LOADING,
      payload: payloadValue,
    });

  const UpdateTypes = {
    PERSONAL_INFO: "personalInfo", //* From the retrieveQuote API
    POLICY_INFO: "policyInfo", //* From the policyOutcome API
  };

  const underwritingQuestions = ({ data }) => {
    let returnObj = {
      dob: new Date(data?.underwritingQuestions?.dateOfBirth) || null,
      gender: data?.underwritingQuestions?.gender,
      tobaccoQuestion:
        data?.underwritingQuestions?.hasSmokedLast12Months === true
          ? "yes"
          : "no",
      residentQuestion:
        data?.underwritingQuestions?.permanentResidentOfAustralia === true
          ? "yes"
          : "no",
      termsAndConditions: data?.underwritingQuestions?.acceptTermsAndConditions,
      otherAllianzInsuranceQuestion:
        data?.underwritingQuestions?.hasOtherAllianzInsurance === true
          ? "yes"
          : "no",
      otherAllianzInsuranceList:
        data?.underwritingQuestions?.hasOtherAllianzInsurance === true
          ? matchInsuranceList(
              data?.underwritingQuestions?.otherAllianzProducts.toLowerCase()
            )
          : [],
      occupationList: matchOccupationList(
        data?.underwritingQuestions?.occupationCode
      ),
    };
    return returnObj;
  };

  const updateStateOnRetrieval = async ({ data, updateType }) => {
    let mappedValuesFour = {};
    switch (updateType) {
      case UpdateTypes.PERSONAL_INFO:
        mappedValuesFour = {
          ...state,
          personOne: {
            ...state.personOne,
            title:
              data?.insuredParticipants[0]?.basicDetails?.title.toLowerCase(),
            firstName: data?.insuredParticipants[0]?.basicDetails?.firstName,
            surname: data?.insuredParticipants[0]?.basicDetails?.surname,
            promoCode: data?.general?.discounts?.promotionalCode,
            emailAddress: data?.general?.basicDetails?.email,
            contactNumber: data?.general?.basicDetails?.telephone.mobile,
            callbackConsent:
              isObjectEmpty(data?.general?.declarations) === true ||
              data?.general?.declarations?.hasFollowUpCall == null
                ? null
                : data?.general?.declarations?.hasFollowUpCall === true
                  ? "yes"
                  : "no",
            state:
              data?.general?.basicDetails?.residentialAddress.state.toLowerCase(),
            ...underwritingQuestions({ data: data?.insuredParticipants[0] }),
          },
          personTwo: {
            ...state.personTwo,
            title: data?.insuredParticipants[1]?.basicDetails?.title || "",
            firstName:
              data?.insuredParticipants[1]?.basicDetails?.firstName || "",
            surname: data?.insuredParticipants[1]?.basicDetails?.surname || "",
          },
          promoCodeValid:
            data?.general?.discounts?.promotionalCode?.length > 0
              ? true
              : false,
          customerRetrieveQuoteLoaded: true, //* Allows us to check if the data was loaded from the API
        };
        mappedValuesFour.personOne = ageUpdate(
          mappedValuesFour.personOne,
          mappedValuesFour.personOne.dob,
          state.application
        );
        break;
      case UpdateTypes.POLICY_INFO:
        mappedValuesFour = {
          ...state,
          quoteNumber: data?.quoteNumber,
          personOne: {
            ...state.personOne,
            ...ageUpdate(
              state.personOne,
              new Date(data?.underwritingQuestions?.dateOfBirth),
              state.application
            ),
            firstName: data?.firstName,
            surname: data?.surname,
            title: data?.title,
            lifeCover: data?.covers?.life?.sumInsured,
            illnessCover:
              (data?.covers?.hasCriticalIllnessCover === true ||
                data?.covers?.hasCriticalIllnessUpSell === true) &&
              data?.covers?.hasCriticalIllnessDecline === false
                ? data?.covers?.criticalIllness.sumInsured
                : "",
            shouldUseIllnessCover: data?.covers?.hasCriticalIllnessUpSell
              ? false
              : data?.covers?.hasCriticalIllnessCover
                ? true
                : false,
            permanentDisabilityCover:
              data?.covers?.hasUnableToWorkCover === true &&
              data?.covers?.hasUnableToWorkDecline === false
                ? data?.covers?.unableToWork.sumInsured
                : "",
            traumaCover:
              data?.covers?.hasCriticalIllnessCover === true ? "no" : "",
            ...underwritingQuestions({ data: data }),
          },
          auraOutcomeLoaded: true, //* Allows us to check if the data was loaded from the API
        };
        break;
      default:
        return;
    }
    dispatchWithCallback({
      type: types.UPDATE_DATA,
      payload: { ...mappedValuesFour },
    });
  };

  // when Retrieval happens via CC search or Online Retrieval when the state is completely empty
  const updateStateOnFreshRetrieval = async ({ data }) => {
    let remappedStateValue = {};
    let temp = isObjectEmpty(data?.general?.declarations);
    remappedStateValue = {
      ...state,
      personOne: {
        ...state.personOne,
        // updating personal info
        title: data?.insuredParticipants[0]?.basicDetails?.title.toLowerCase(),
        firstName: data?.insuredParticipants[0]?.basicDetails?.firstName,
        surname: data?.insuredParticipants[0]?.basicDetails?.surname,
        promoCode: data?.general?.discounts?.promotionalCode,
        emailAddress: data?.general?.basicDetails?.email,
        contactNumber: data?.general?.basicDetails?.telephone.mobile,
        callbackConsent:
          isObjectEmpty(data?.general?.declarations) === true ||
          data?.general?.declarations?.hasFollowUpCall == null
            ? null
            : data?.general?.declarations?.hasFollowUpCall === true
              ? "yes"
              : "no",
        state:
          data?.general?.basicDetails?.residentialAddressSameAs == true
            ? data?.general?.basicDetails?.postalAddress?.state?.toLowerCase()
            : data?.general?.basicDetails?.residentialAddress?.state?.toLowerCase(),
        dob:
          new Date(
            data?.insuredParticipants[0]?.underwritingQuestions?.dateOfBirth
          ) || null,
        gender:
          data?.insuredParticipants[0]?.underwritingQuestions?.gender.toLowerCase() ===
          "m"
            ? "male"
            : "female",
        // updating policy info
        lifeCover: data?.covers?.life?.sumInsured,
        coverDetails: {
          anyExclusions: data?.covers?.anyExclusions,
          anyDeclined: data?.covers?.anyDeclined,
          anyLoading: data?.covers?.anyLoading,
          hasLifeCover: data?.covers?.hasLifeCover,
          hasLifeDecline: data?.covers?.hasLifeDecline,
          hasLifeLoading: data?.covers?.hasLifeLoading,
          hasLifeExclusion: data?.covers?.hasLifeExclusion,
          hasLifeExclusionOrLoadings: data?.covers?.hasLifeExclusionOrLoadings,
        },
        illnessCover:
          data?.covers?.hasCriticalIllnessCover === true
            ? data?.covers?.criticalIllness.sumInsured
            : data?.covers?.hasCriticalIllnessUpSell === true
              ? data?.upsell.amount
              : "",
        shouldUseIllnessCover:
          data?.covers?.hasCriticalIllnessUpSell === true ? false : true,
        permanentDisabilityCover:
          data?.covers?.hasUnableToWorkCover === true
            ? data?.covers?.unableToWork.sumInsured
            : "",
        tobaccoQuestion:
          data?.underwritingQuestions?.hasSmokedLast12Months === true
            ? "yes"
            : "no",
        residentQuestion:
          data?.underwritingQuestions?.permanentResidentOfAustralia === true
            ? "yes"
            : "no",
        termsAndConditions:
          data?.underwritingQuestions?.acceptTermsAndConditions,
        otherAllianzInsuranceQuestion:
          data?.underwritingQuestions?.hasOtherAllianzInsurance === true
            ? "yes"
            : "no",
        traumaCover:
          data?.covers?.hasCriticalIllnessCover === true ? "no" : null,
        occupationList: matchOccupationList(
          data?.underwritingQuestions?.occupationCode
        ),
        otherAllianzInsuranceList: data?.underwritingQuestions
          ?.otherAllianzProducts
          ? matchInsuranceList(
              data?.underwritingQuestions?.otherAllianzProducts.toLowerCase()
            )
          : [],
      },
      personTwo: {
        ...state.personTwo,
        title: data?.insuredParticipants[1]?.basicDetails?.title || "",
        firstName: data?.insuredParticipants[1]?.basicDetails?.firstName || "",
        surname: data?.insuredParticipants[1]?.basicDetails?.surname || "",
      },
      customerRetrieveQuoteLoaded: true, //* Allows us to check if the data was loaded from the API
      quoteNumber: data?.quoteNumber,
      promoCodeValid:
        data?.general?.discounts?.promotionalCode?.length > 0 ? true : false,
      auraOutcomeLoaded: true, //* Allows us to check if the data was loaded from the API
    };
    if (isAdditionalPolicyPresentRetrieve(data?.general?.additionalOwner)) {
      remappedStateValue.additionalPolicyHolderTitle =
        data?.general?.additionalOwner?.title.toLowerCase();
      remappedStateValue.additionalPolicyHolderDOB =
        new Date(data?.general?.additionalOwner?.dateOfBirth) || null;
      remappedStateValue.additionalPolicyHolderFirstName =
        data?.general?.additionalOwner?.firstName;
      remappedStateValue.additionalPolicyHolderSurname =
        data?.general?.additionalOwner?.surname;
    }
    remappedStateValue.personOne = ageUpdate(
      remappedStateValue.personOne,
      new Date(data?.insuredParticipants[0]?.underwritingQuestions?.dateOfBirth)
    );
    dispatchWithCallback({
      type: types.UPDATE_DATA,
      payload: { ...remappedStateValue },
    });
  };

  //*** Additional Conditions Loadings and Exclusions */
  //* Set the state to updating.
  const setAdditionalConditionsUpdating = (payloadValue) =>
    dispatchWithCallback({
      type: types.UPDATE_ADDITIONAL_CONDITIONS_LOADING,
      payload: payloadValue,
    });

  const updateAdditionalConditions = async (values) => {
    if (isObjEmpty(state) || values == null) return;

    if (!isObjEmpty(state.additionalConditions)) {
      setAdditionalConditionsUpdating(true);
    }
    await new Promise((resolve) => setTimeout(resolve, 0));

    let mappedValues = { ...state };
    const { fieldName } = { ...values };

    let indexValue = 0;
    switch (fieldName) {
      case "additionalConditionsLife":
        indexValue = 0;
        break;
      case "additionalConditionsCriticalIllness":
        indexValue = 1;
        break;
      case "additionalConditionsUnableToWork":
        indexValue = 2;
        break;
    }

    switch (values.type) {
      case "update_loading":
        const amt =
          values?.data != null
            ? parseFloat(currencyStringToNumber(values?.data), 10)
            : "";

        mappedValues.additionalConditions[indexValue][fieldName].loading = amt;
        break;
      case "update_exclusions":
        const exclusionData = values?.data != null ? values?.data : [];
        mappedValues.additionalConditions[indexValue][
          fieldName
        ].exclusionCodes = exclusionData;
        break;
      default:
        return;
    }

    // checkValueChange("updateAdditionalConditions", mappedValues, values);
    dispatchWithCallback({
      type: types.UPDATE_ADDITIONAL_CONDITIONS,
      payload: { ...mappedValues },
    });
  };
  //*** */

  return (
    <PreQualificationContext.Provider
      value={{
        state,
        handleInputChange, //* Update individual object values
        removeSecondApplicant,
        setValues, //* Update multiple object values
        updateStateOnRetrieval,
        updateStateOnFreshRetrieval,
        initializedPreQual: initialized,
        initialized: initialized,
        initialState: initialStateData,
        dataSet: state,
        funcSet: {
          initializing,
          // resetData,
          updateCriticalIllness,
          updatePermanentDisabilityCover,
          updateAdditionalConditions,
        },
      }}
    >
      {props.children}
    </PreQualificationContext.Provider>
  );
};

export default PreQualificationState;
